import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Container,
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  useTheme,
  useMediaQuery,
  alpha,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import {
  Info as InfoIcon,
  PieChart as PieChartIcon,
  Flag as FlagIcon,
  TrendingUp as TrendingUpIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import axios from "axios";
import Utils from "../util/Utils";
import StockChart from "./StockChart";
import Constants, { API_KEYS, FMP_API_ENDPOINTS } from "../util/Constants";
import CompanyPage from "./CompanyPage";

// ── Design tokens (matching CompanyProfile) ──────────────────────────────────
const C = {
  textPrimary:   "#0f1f35",
  textSecondary: "#3d5166",
  textMuted:     "#7a92a8",
  bg:            "#ffffff",
  card:          "#ffffff",
  cardHeader:    "#f5f2ed",       // warm cream/beige, soft and professional
  cardHeaderText: "#1a2744",       // deep navy, clear and readable
  border:        "#d0dcea",
  borderLight:   "#e8eef6",
  navyDark:      "#0f2d52",
  navy:          "#1a4075",
  navyMid:       "#2c5282",
  navyLight:     "#dce8f5",
  success:       "#0a8a7e",
  successBg:     "#e6f9f7",
  danger:        "#b91c1c",
  dangerBg:      "#fde8e8",
  warning:       "#b45309",
};

const MONO = "'DM Mono', 'Fira Mono', 'Courier New', monospace";
const SANS = "'Lato', 'Helvetica Neue', Arial, sans-serif";

// ── Styled components (matching CompanyProfile) ─────────────────────────────
const StyledCard = styled(Card)(({ theme }) => ({
  fontFamily: SANS,
  height: "100%",
  width: "100%",
  border: `1px solid ${C.border}`,
  borderRadius: "10px",
  boxShadow: "0 1px 3px rgba(15,45,82,0.06)",
  overflow: "hidden",
  transition: "box-shadow 0.15s",
  backgroundColor: "#ffffff",
  marginBottom: theme.spacing(0.5),
  "&:hover": {
    boxShadow: "0 4px 12px rgba(15,45,82,0.1)",
  },
  "& .MuiCardContent-root": {
    padding: theme.spacing(1),
    height: "100%",
    display: "flex",
    flexDirection: "column",
    "&:last-child": {
      paddingBottom: theme.spacing(1),
    },
  },
}));

const ChartCard = styled(StyledCard)(({ theme }) => ({
  minHeight: "260px",
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${C.border}`,
  backgroundColor: theme.palette.background.paper,
}));

const AboutCard = styled(StyledCard)(({ theme }) => ({
  minHeight: "60px",
  height: "auto",
  transition: "height 0.3s ease-in-out",
  "&.expanded": {
    height: "auto",
    minHeight: "60px",
  },
  "& .MuiCardContent-root": {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    padding: theme.spacing(0.5, 1),
    "&:last-child": {
      paddingBottom: theme.spacing(0.5),
    },
  },
  "& .MuiAccordion-root": {
    boxShadow: "none",
    "&:before": {
      display: "none",
    },
  },
  "& .MuiAccordionSummary-root": {
    padding: 0,
    minHeight: "unset",
  },
  "& .MuiAccordionSummary-content": {
    margin: 0,
    "& .MuiTypography-root": {
      display: "-webkit-box",
      WebkitLineClamp: 2,
      WebkitBoxOrient: "vertical",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
  },
  "& .MuiAccordionDetails-root": {
    padding: theme.spacing(0.5, 0),
  },
}));

// Table-only cards: minimal padding so tables sit close to card edges
const TableCard = styled(StyledCard)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  "& .MuiCardContent-root": {
    padding: 0,
    flex: 1,
    display: "flex",
    flexDirection: "column",
    "&:last-child": {
      paddingBottom: 0,
    },
  },
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  fontFamily: SANS,
  overflow: "auto",
  width: "100%",
  height: "100%",
  "&::-webkit-scrollbar": {
    width: "0px",
    background: "transparent",
  },
  "& .MuiTableCell-root": {
    padding: theme.spacing(0.35, 1.25),
    fontSize: "0.875rem",
    lineHeight: 1.25,
    fontFamily: SANS,
  },
  "& .MuiTableRow-root:hover": {
    backgroundColor: C.cardHeader,
  },
}));

const ETFProfile = ({ stockValues }) => {
  const [holdingsData, setHoldingsData] = useState([]);
  const [sectorChartData, setSectorChartData] = useState([]);
  const [countryData, setCountryData] = useState([]);
  const [etfProfile, setEtfProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSymbol, setSelectedSymbol] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [aboutExpanded, setAboutExpanded] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const symbol = stockValues.compProfile.symbol;
        const [
          holdingsResponse,
          sectorResponse,
          countryResponse,
          etfProfileResponse,
        ] = await Promise.all([
          axios.get(
            `${FMP_API_ENDPOINTS.ETF_HOLDINGS(symbol)}?${API_KEYS.FMP_API_KEY}`
          ),
          axios.get(
            `${FMP_API_ENDPOINTS.ETF_SECTOR(symbol)}?${API_KEYS.FMP_API_KEY}`
          ),
          axios.get(
            `${FMP_API_ENDPOINTS.ETF_COUNTRY(symbol)}?${API_KEYS.FMP_API_KEY}`
          ),
          axios.get(
            `${FMP_API_ENDPOINTS.ETF_PROFILE(symbol)}&${API_KEYS.FMP_API_KEY}`
          ),
        ]);

        const processedSectorData = sectorResponse.data
          .map((d) => {
            const raw = d.weightPercentage;
            const n =
              typeof raw === "string"
                ? parseFloat(String(raw).replace(/%/g, "").trim())
                : typeof raw === "number"
                  ? raw
                  : NaN;
            const value = Number.isFinite(n) ? n : 0;
            return {
              name: d.sector,
              value,
              weightPercentage: Utils.formatAllocationWeight(raw),
            };
          })
          .sort((a, b) => b.value - a.value);

        const processedHoldingsData = holdingsResponse.data
          .slice(0, 15)
          .sort((a, b) => b.weightPercentage - a.weightPercentage);

        setHoldingsData(processedHoldingsData);
        setSectorChartData(processedSectorData);
        setCountryData(countryResponse.data);
        setEtfProfile(etfProfileResponse.data[0]);
      } catch (error) {
        console.error("Error fetching ETF data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [stockValues.compProfile.symbol]);

  const handleSymbolClick = (symbol) => {
    setSelectedSymbol(symbol);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedSymbol("");
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 2, mt: 1 }}>
      <Grid container spacing={1}>
        {/* First Row: About (Full Width) */}
        <Grid item xs={12} sx={{ display: "flex" }}>
          <AboutCard className={aboutExpanded ? "expanded" : ""} sx={{ width: "100%" }}>
            <CardContent>
              {(() => {
                const full = stockValues.compProfile.description || "No description available.";
                const PREVIEW_LEN = 400;
                let preview = "";
                let remainder = "";
                if (full.length <= PREVIEW_LEN) {
                  preview = full;
                } else {
                  const chunk = full.slice(0, PREVIEW_LEN);
                  const lastSpace = chunk.lastIndexOf(" ");
                  preview = lastSpace > 100 ? chunk.slice(0, lastSpace) : chunk;
                  remainder = full.slice(preview.length).trim();
                }
                return (
                  <Accordion
                    expanded={aboutExpanded}
                    onChange={(event, expanded) => {
                      setAboutExpanded(expanded);
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        padding: 0,
                        minHeight: "unset",
                        "& .MuiAccordionSummary-content": {
                          margin: 0,
                        },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontFamily: SANS,
                          fontSize: "1rem",
                          lineHeight: 1.4,
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {preview}{remainder ? " …" : ""}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ padding: 0 }}>
                      <Typography
                        variant="body1"
                        sx={{
                          fontFamily: SANS,
                          fontSize: "1rem",
                          lineHeight: 1.4,
                        }}
                      >
                        {remainder || full}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                );
              })()}
            </CardContent>
          </AboutCard>
        </Grid>

        {/* Second Row: Key Info, Performance, Country Weightings */}
        <Grid item xs={12} md={4} sx={{ display: "flex" }}>
          <TableCard sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column" }}>
            <CardContent sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden", p: 0 }}>
              <Box sx={{ 
                display: "flex", 
                alignItems: "center", 
                gap: 1.25, 
                px: 1.5, 
                py: 1, 
                borderBottom: `1px solid ${C.borderLight}`, 
                backgroundColor: C.cardHeader 
              }}>
                <InfoIcon sx={{ color: C.navy, fontSize: "1.2rem" }} />
                <Typography sx={{ 
                  fontWeight: 700, 
                  fontSize: "1rem", 
                  color: C.cardHeaderText, 
                  fontFamily: SANS 
                }}>
                  Key Information
                </Typography>
              </Box>
              <StyledTableContainer component={Paper} elevation={0} sx={{ flex: 1, minHeight: 0, overflow: "auto", maxHeight: "none" }}>
                <Table size="small">
                  <TableBody>
                    {[
                      {
                        label: "Exchange",
                        value: stockValues.compProfile.exchangeShortName || "N/A",
                      },
                      {
                        label: "Market Cap",
                        value: stockValues.compProfile.marketCap
                          ? `${Utils.getCompanySize(
                              stockValues.compProfile.marketCap
                            )} (${Utils.format_currency_short(
                              stockValues.compProfile.marketCap
                            )})`
                          : "N/A",
                      },
                      {
                        label: "Avg Volume",
                        value: stockValues.compProfile.volAvg
                          ? Utils.format_currency_short(stockValues.compProfile.volAvg)
                          : "N/A",
                      },
                      {
                        label: "52W High",
                        value:
                          stockValues.compProfile.range &&
                          stockValues.compProfile.range.includes("-")
                            ? Utils.format_currency_usd(
                                stockValues.compProfile.range.split("-")[1]
                              )
                            : "N/A",
                      },
                      {
                        label: "52W Low",
                        value:
                          stockValues.compProfile.range &&
                          stockValues.compProfile.range.includes("-")
                            ? Utils.format_currency_usd(
                                stockValues.compProfile.range.split("-")[0]
                              )
                            : "N/A",
                      },
                      {
                        label: "Expense Ratio",
                        value: etfProfile?.expenseRatio
                          ? `${(etfProfile.expenseRatio * 100).toFixed(2)}%`
                          : "N/A",
                      },
                      {
                        label: "Inception Date",
                        value: etfProfile?.inceptionDate || "N/A",
                      },
                      {
                        label: "Holdings Count",
                        value: etfProfile?.holdingsCount || "N/A",
                      },
                    ].map((row, index) => (
                      <TableRow key={index}>
                        <TableCell 
                          component="th" 
                          scope="row" 
                          sx={{ 
                            fontWeight: 600, 
                            fontSize: "0.875rem", 
                            color: C.textSecondary, 
                            fontFamily: SANS, 
                            px: 1.5 
                          }}
                        >
                          {row.label}
                        </TableCell>
                        <TableCell 
                          align="right" 
                          sx={{ 
                            fontSize: "0.875rem", 
                            fontWeight: 600, 
                            color: C.textPrimary, 
                            fontFamily: MONO, 
                            fontVariantNumeric: "tabular-nums", 
                            whiteSpace: "nowrap", 
                            px: 1.5 
                          }}
                        >
                          {row.value}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </StyledTableContainer>
            </CardContent>
          </TableCard>
        </Grid>

        <Grid item xs={12} md={4} sx={{ display: "flex" }}>
          <TableCard sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column" }}>
            <CardContent sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden", p: 0 }}>
              <Box sx={{ 
                display: "flex", 
                alignItems: "center", 
                gap: 1.25, 
                px: 1.5, 
                py: 1, 
                borderBottom: `1px solid ${C.borderLight}`, 
                backgroundColor: C.cardHeader 
              }}>
                <TrendingUpIcon sx={{ color: C.navy, fontSize: "1.2rem" }} />
                <Typography sx={{ 
                  fontWeight: 700, 
                  fontSize: "1rem", 
                  color: C.cardHeaderText, 
                  fontFamily: SANS 
                }}>
                  Performance
                </Typography>
              </Box>
              <StyledTableContainer component={Paper} elevation={0} sx={{ flex: 1, minHeight: 0, overflow: "auto", maxHeight: "none" }}>
                <Table size="small">
                  <TableBody>
                    {(() => {
                      const r = stockValues.stockReport;
                      const pick = (...keys) => {
                        for (const k of keys) {
                          const n = Utils.parsePerfNumber(r[k]);
                          if (n != null) return n;
                        }
                        return null;
                      };
                      const perfRows = [
                        { label: "1 Day", raw: pick("perfDay") },
                        { label: "1 Week", raw: pick("perfWeek") },
                        { label: "1 Month", raw: pick("perfMonth") },
                        {
                          label: "3 Month",
                          raw: pick("perfQuart", "perf3Month"),
                        },
                        { label: "6 Month", raw: pick("perf6Month") },
                        { label: "YTD", raw: pick("perfYTD") },
                        {
                          label: "1 Year",
                          raw: pick("perfYear", "perf1YearTotal"),
                        },
                        { label: "3Y (Actual return)", raw: pick("perf3YearTotal") },
                        { label: "5Y (Actual return)", raw: pick("perf5YearTotal") },
                        {
                          label: "10Y (Actual return)",
                          raw: pick("perf10YearTotal"),
                        },
                      ];
                      return perfRows.map((row, index) => {
                        const valueStr = Utils.format_ratio(row.raw);
                        const color =
                          row.raw == null
                            ? C.textSecondary
                            : row.raw < 0
                              ? C.danger
                              : C.success;
                        return (
                          <TableRow key={index}>
                            <TableCell
                              component="th"
                              scope="row"
                              sx={{
                                fontWeight: 600,
                                fontSize: "0.875rem",
                                color: C.textSecondary,
                                fontFamily: SANS,
                                px: 1.5,
                              }}
                            >
                              {row.label}
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                fontSize: "0.875rem",
                                fontWeight: 700,
                                color,
                                fontFamily: MONO,
                                fontVariantNumeric: "tabular-nums",
                                whiteSpace: "nowrap",
                                px: 1.5,
                              }}
                            >
                              {valueStr}
                            </TableCell>
                          </TableRow>
                        );
                      });
                    })()}
                  </TableBody>
                </Table>
              </StyledTableContainer>
            </CardContent>
          </TableCard>
        </Grid>

        <Grid item xs={12} md={4} sx={{ display: "flex" }}>
          {countryData.length > 0 && (
            <TableCard sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column" }}>
              <CardContent sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden", p: 0 }}>
                <Box sx={{ 
                  display: "flex", 
                  alignItems: "center", 
                  gap: 1.25, 
                  px: 1.5, 
                  py: 1, 
                  borderBottom: `1px solid ${C.borderLight}`, 
                  backgroundColor: C.cardHeader 
                }}>
                  <FlagIcon sx={{ color: C.navy, fontSize: "1.2rem" }} />
                  <Typography sx={{ 
                    fontWeight: 700, 
                    fontSize: "1rem", 
                    color: C.cardHeaderText, 
                    fontFamily: SANS 
                  }}>
                    Country Weightings
                  </Typography>
                </Box>
                <StyledTableContainer component={Paper} elevation={0} sx={{ flex: 1, minHeight: 0, overflow: "auto", maxHeight: "none" }}>
                  <Table size="small">
                    <TableBody>
                      {countryData.map((country, index) => (
                        <TableRow key={index}>
                          <TableCell 
                            component="th" 
                            scope="row" 
                            sx={{ 
                              fontWeight: 600, 
                              fontSize: "0.875rem", 
                              color: C.textSecondary, 
                              fontFamily: SANS, 
                              px: 1.5 
                            }}
                          >
                            {country.country}
                          </TableCell>
                          <TableCell 
                            align="right" 
                            sx={{ 
                              fontSize: "0.875rem", 
                              fontWeight: 600, 
                              color: C.textPrimary, 
                              fontFamily: MONO, 
                              fontVariantNumeric: "tabular-nums", 
                              whiteSpace: "nowrap", 
                              px: 1.5 
                            }}
                          >
                            {country.weightPercentage === 10000
                              ? "N/A"
                              : country.weightPercentage}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </StyledTableContainer>
              </CardContent>
            </TableCard>
          )}
        </Grid>

        {/* Third Row: Chart, Sector Allocation, and Holdings */}
        <Grid item xs={12} md={6} sx={{ display: "flex" }}>
          <ChartCard sx={{ width: "100%" }}>
            <CardContent>
              <Box sx={{ 
                display: "flex", 
                alignItems: "center", 
                justifyContent: "center",
                gap: 1.25, 
                px: 1.5, 
                py: 1, 
                mb: 0.5,
                borderBottom: `1px solid ${C.borderLight}`,
                backgroundColor: C.cardHeader,
              }}>
                <Typography sx={{ 
                  fontWeight: 700, 
                  fontSize: "1rem", 
                  color: C.cardHeaderText, 
                  fontFamily: SANS,
                  textAlign: "center"
                }}>
                  Price Chart
                </Typography>
              </Box>
              <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", minHeight: 200, p: 1 }}>
                <StockChart symbol={stockValues.compProfile.symbol} />
              </Box>
            </CardContent>
          </ChartCard>
        </Grid>

        <Grid item xs={12} md={3} sx={{ display: "flex" }}>
          {sectorChartData.length > 0 && (
            <TableCard sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column" }}>
              <CardContent sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden", p: 0 }}>
                <Box sx={{ 
                  display: "flex", 
                  alignItems: "center", 
                  gap: 1.25, 
                  px: 1.5, 
                  py: 1, 
                  borderBottom: `1px solid ${C.borderLight}`, 
                  backgroundColor: C.cardHeader 
                }}>
                  <PieChartIcon sx={{ color: C.navy, fontSize: "1.2rem" }} />
                  <Typography sx={{ 
                    fontWeight: 700, 
                    fontSize: "1rem", 
                    color: C.cardHeaderText, 
                    fontFamily: SANS 
                  }}>
                    Sector Allocation
                  </Typography>
                </Box>
                <StyledTableContainer component={Paper} elevation={0} sx={{ flex: 1, minHeight: 0, overflow: "auto", maxHeight: "none" }}>
                  <Table size="small">
                    <TableBody>
                      {sectorChartData.map((sector, index) => (
                        <TableRow key={index}>
                          <TableCell 
                            component="th" 
                            scope="row" 
                            sx={{ 
                              fontWeight: 600, 
                              fontSize: "0.875rem", 
                              color: C.textSecondary, 
                              fontFamily: SANS, 
                              px: 1.5 
                            }}
                          >
                            {sector.name}
                          </TableCell>
                          <TableCell 
                            align="right" 
                            sx={{ 
                              fontSize: "0.875rem", 
                              fontWeight: 600, 
                              color: C.textPrimary, 
                              fontFamily: MONO, 
                              fontVariantNumeric: "tabular-nums", 
                              whiteSpace: "nowrap", 
                              px: 1.5 
                            }}
                          >
                            {sector.weightPercentage}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </StyledTableContainer>
              </CardContent>
            </TableCard>
          )}
        </Grid>

        <Grid item xs={12} md={3} sx={{ display: "flex" }}>
          {holdingsData.length > 0 && (
            <TableCard sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column" }}>
              <CardContent sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden", p: 0 }}>
                <Box sx={{ 
                  display: "flex", 
                  alignItems: "center", 
                  gap: 1.25, 
                  px: 1.5, 
                  py: 1, 
                  borderBottom: `1px solid ${C.borderLight}`, 
                  backgroundColor: C.cardHeader 
                }}>
                  <TrendingUpIcon sx={{ color: C.navy, fontSize: "1.2rem" }} />
                  <Typography sx={{ 
                    fontWeight: 700, 
                    fontSize: "1rem", 
                    color: C.cardHeaderText, 
                    fontFamily: SANS 
                  }}>
                    Top 15 Holdings
                  </Typography>
                </Box>
                <StyledTableContainer component={Paper} elevation={0} sx={{ flex: 1, minHeight: 0, overflow: "auto", maxHeight: "none" }}>
                  <Table size="small">
                    <TableBody>
                      {holdingsData.map((holding, index) => (
                        <TableRow
                          key={index}
                          onClick={() => handleSymbolClick(holding.asset)}
                          sx={{ cursor: "pointer" }}
                        >
                          <TableCell sx={{ px: 1.5 }}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              {holding.asset && (
                                <Box
                                  component="img"
                                  src={`${Constants.IMAGE_URL}${holding.asset}.jpg`}
                                  onError={(e) => { e.target.style.display = "none"; }}
                                  sx={{
                                    width: 24,
                                    height: 24,
                                    objectFit: "contain",
                                    borderRadius: "4px",
                                  }}
                                />
                              )}
                              <Typography
                                variant="body2"
                                sx={{ 
                                  fontSize: "0.875rem", 
                                  fontFamily: SANS,
                                  fontWeight: 600,
                                  color: C.textSecondary,
                                }}
                              >
                                {holding.asset}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell 
                            align="right" 
                            sx={{ 
                              fontSize: "0.875rem", 
                              fontWeight: 700, 
                              color: C.textPrimary, 
                              fontFamily: MONO, 
                              fontVariantNumeric: "tabular-nums", 
                              whiteSpace: "nowrap", 
                              px: 1.5 
                            }}
                          >
                            {Utils.formatAllocationWeight(holding.weightPercentage)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </StyledTableContainer>
              </CardContent>
            </TableCard>
          )}
        </Grid>
      </Grid>

      {/* Holdings Detail Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleDialogClose}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            minHeight: "80vh",
            maxHeight: "90vh",
          },
        }}
      >
        <DialogContent>
          {selectedSymbol && <CompanyPage symbol={selectedSymbol} />}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ETFProfile;